import React from "react";
import {
  Plane, Building2, Car, Package as Pkg, MapPin, Clock, Briefcase,
  Utensils, Armchair, ShieldCheck, User, Moon, Calendar
} from "lucide-react";
import { formatCheckedBaggage } from "@/lib/bagFormat";
import { useCurrency } from "@/lib/currency";

function fmtDur(min) { const h = Math.floor(min / 60), m = min % 60; return `${h}h ${m}m`; }

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-4 h-4 text-[var(--brand-primary)]" />}
        <h3 className="font-heading text-sm font-semibold text-[var(--brand-accent)] uppercase tracking-wider">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function FlightView({ item, travelers }) {
  const segs = item?.segments || [];
  const anc = item?.ancillaries || {};
  const { format: fmt } = useCurrency();
  return (
    <div className="space-y-5">
      <Section icon={Plane} title="Itinerary">
        <div className="space-y-3">
          {segs.map((s, i) => (
            <div key={i} className="p-3 border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center text-[var(--brand-primary)] font-bold text-xs">{s.airline_code}</div>
                  <div>
                    <div className="font-medium text-[var(--brand-accent)]">{s.airline_name} · {s.flight_number}</div>
                    <div className="text-xs text-slate-500">{s.aircraft} · {s.date}</div>
                  </div>
                </div>
                {s.fare && <span className="text-[10px] uppercase tracking-wider bg-[var(--brand-secondary)] text-[#064e3b] font-bold px-2 py-0.5 rounded-full">{s.fare.name} fare</span>}
              </div>
              <div className="grid grid-cols-7 gap-3 items-center mt-3">
                <div className="col-span-2">
                  <div className="font-heading text-xl font-semibold">{s.depart_time}</div>
                  <div className="text-xs text-slate-500 font-mono">{s.from}</div>
                </div>
                <div className="col-span-3 flex flex-col items-center text-xs text-slate-500">
                  <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{fmtDur(s.duration_min)}</div>
                  <div className="w-full border-t border-dashed border-slate-300 my-1" />
                  <div>{s.stops === 0 ? "Non-stop" : `${s.stops} stop${s.stops > 1 ? "s" : ""}`}</div>
                </div>
                <div className="col-span-2 text-right">
                  <div className="font-heading text-xl font-semibold">{s.arrive_time}</div>
                  <div className="text-xs text-slate-500 font-mono">{s.to}</div>
                </div>
              </div>
              {s.fare && (
                <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
                  <span className="inline-flex items-center gap-1 bg-slate-50 px-2 py-1 rounded"><Briefcase className="w-3 h-3 text-[var(--brand-primary)]" /> Cabin {s.fare.cabin_baggage_kg}kg</span>
                  <span className="inline-flex items-center gap-1 bg-slate-50 px-2 py-1 rounded"><Briefcase className="w-3 h-3 text-[var(--brand-primary)]" /> Check-in {formatCheckedBaggage(s.fare.checked_baggage_qty, s.fare.checked_baggage_kg)}</span>
                  {s.fare.meal && <span className="inline-flex items-center gap-1 bg-slate-50 px-2 py-1 rounded"><Utensils className="w-3 h-3 text-[var(--brand-primary)]" /> Meal</span>}
                  {s.fare.seat_selection && <span className="inline-flex items-center gap-1 bg-slate-50 px-2 py-1 rounded"><Armchair className="w-3 h-3 text-[var(--brand-primary)]" /> Free seat</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {travelers?.length > 0 && (
        <Section icon={User} title="Travelers">
          <div className="rounded-xl border border-slate-100 overflow-hidden">
            {/* Proper HTML table so columns share the full container width via
                `table-fixed` + explicit column widths — replaces the 12-col grid
                that was leaving a trailing gap on the right. */}
            <table className="w-full table-fixed">
              <colgroup>
                <col style={{ width: "5%" }} />
                <col style={{ width: "26%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "22%" }} />
              </colgroup>
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Passenger</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Gender / DOB</th>
                  <th className="px-3 py-2 text-left">Nationality</th>
                  <th className="px-3 py-2 text-left">Passport</th>
                </tr>
              </thead>
              <tbody>
                {travelers.map((t, i) => {
                  const typeUp = (t.type || "").toUpperCase();
                  const paxType = (typeUp === "CHD" || typeUp === "CHILD") ? "Child"
                    : (typeUp === "INF" || typeUp === "INFANT") ? "Infant" : "Adult";
                  const gender = t.gender === "F" ? "Female" : "Male";
                  return (
                    <tr key={i} className="border-t border-slate-100 align-top text-sm">
                      <td className="px-3 py-2.5 text-slate-400">{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium text-[var(--brand-accent)] break-words">
                        {[t.title, t.first_name, t.last_name].filter(Boolean).join(" ")}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{paxType}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">
                        <div>{gender}</div>
                        {t.dob && <div className="text-slate-400">DOB {t.dob}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{t.nationality || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-xs font-mono break-all">
                        {t.passport_number || "—"}
                        {t.passport_expiry && <div className="text-[10px] text-slate-400 mt-0.5">Exp {t.passport_expiry}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {(anc.seats || anc.bags || anc.meals || anc.insurance) && (
        <Section icon={ShieldCheck} title="Add-ons">
          <div className="text-sm space-y-1.5">
            {anc.insurance && <div className="flex items-center gap-1.5 text-[var(--brand-accent)]"><ShieldCheck className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> {anc.insurance.name}</div>}
            {Object.keys(anc.seats || {}).length > 0 && (
              <div><span className="font-medium">Seats:</span> <span className="text-slate-600">{Object.values(anc.seats).map((v) => v.category).join(", ")}</span></div>
            )}
            {Object.keys(anc.bags || {}).length > 0 && (
              <div>
                <span className="font-medium">Extra baggage:</span>
                <div className="text-slate-600 text-xs mt-1 space-y-0.5">
                  {Object.entries(anc.bags).map(([k, v]) => {
                    const [segIdx, paxIdx] = String(k).split("-");
                    const trav = travelers?.[Number(paxIdx)];
                    const who = trav ? `${trav.first_name} ${trav.last_name}` : `Passenger ${Number(paxIdx) + 1}`;
                    const label = formatCheckedBaggage(v.qty, v.kg);
                    const price = Number(v.totalPrice || 0);
                    return (
                      <div key={k}>• Segment {Number(segIdx) + 1} · {who}: {label}{price > 0 ? ` (+${fmt(price)})` : ""}</div>
                    );
                  })}
                </div>
              </div>
            )}
            {Object.keys(anc.meals || {}).length > 0 && (
              <div><span className="font-medium">Meals:</span> <span className="text-slate-600">{[...new Set(Object.values(anc.meals).map((v) => v.name))].join(", ")}</span></div>
            )}
          </div>
        </Section>
      )}
    </div>
  );
}

function HotelView({ item }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {item.image && <div className="md:col-span-1 h-36 rounded-xl overflow-hidden"><img src={item.image} alt="" className="w-full h-full object-cover" /></div>}
      <div className="md:col-span-2 space-y-1">
        <div className="font-heading text-xl font-semibold text-[var(--brand-accent)]">{item.name}</div>
        <div className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{item.city}</div>
        <div className="text-sm flex flex-wrap gap-3 mt-2 text-slate-600">
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Check-in: {item.check_in}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Check-out: {item.check_out}</span>
          <span className="flex items-center gap-1"><Moon className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> {item.nights} night{item.nights > 1 ? "s" : ""}</span>
          <span>{item.rooms} room{item.rooms > 1 ? "s" : ""}</span>
        </div>
        {item.amenities && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.amenities.map((a) => <span key={a} className="text-[11px] px-2 py-1 rounded-full bg-teal-50 text-teal-700">{a}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

function CarView({ item }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {item.image && <div className="md:col-span-1 h-36 rounded-xl overflow-hidden"><img src={item.image} alt="" className="w-full h-full object-cover" /></div>}
      <div className="md:col-span-2 space-y-1">
        <div className="font-heading text-xl font-semibold text-[var(--brand-accent)]">{item.model}</div>
        <div className="text-sm text-slate-500">{item.category} · {item.transmission} · {item.seats} seats</div>
        <div className="text-sm flex flex-wrap gap-3 mt-2 text-slate-600">
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[var(--brand-primary)]" />{item.city}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Pickup: {item.pickup_date}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Return: {item.return_date}</span>
          <span>{item.days} day{item.days > 1 ? "s" : ""}</span>
        </div>
      </div>
    </div>
  );
}

function PackageView({ item }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {item.image && <div className="md:col-span-1 h-36 rounded-xl overflow-hidden"><img src={item.image} alt="" className="w-full h-full object-cover" /></div>}
      <div className="md:col-span-2 space-y-1">
        <div className="font-heading text-xl font-semibold text-[var(--brand-accent)]">{item.title}</div>
        <div className="text-sm text-slate-500 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{item.destination} · {item.nights} nights</div>
        {item.inclusions && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.inclusions.map((a) => <span key={a} className="text-[11px] px-2 py-1 rounded-full bg-teal-50 text-teal-700">{a}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

function SightseeingView({ item }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {item.image && <div className="md:col-span-1 h-36 rounded-xl overflow-hidden"><img src={item.image} alt="" className="w-full h-full object-cover" /></div>}
      <div className="md:col-span-2 space-y-1">
        <div className="font-heading text-xl font-semibold text-[var(--brand-accent)]">{item.title}</div>
        <div className="text-sm text-slate-500 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{item.city} · <Clock className="w-3.5 h-3.5" /> {item.duration}</div>
      </div>
    </div>
  );
}

const VIEWS = { flight: FlightView, hotel: HotelView, car: CarView, package: PackageView, sightseeing: SightseeingView };
const ICONS = { flight: Plane, hotel: Building2, car: Car, package: Pkg, sightseeing: MapPin };

export default function BookingItemDetails({ bookingType, item, travelers }) {
  const View = VIEWS[bookingType];
  const Icon = ICONS[bookingType] || Plane;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-teal-50 text-[var(--brand-primary)] flex items-center justify-center"><Icon className="w-4 h-4" /></div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">{bookingType} booking</div>
      </div>
      {View ? <View item={item} travelers={travelers} /> : <div className="text-sm text-slate-500">Booking details unavailable.</div>}
    </div>
  );
}
